import { ThemeProvider } from "@emotion/react";
import styled from "@emotion/styled";
import * as React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Dispatch } from "redux";

import { Check, Edit, PersonAdd } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  Heading,
  Icon,
  ModalPortal,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import { BackButton, Grid, ViewContainer, ViewStack } from "components/grid";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { StyledLink } from "components/links/StyledLink";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { SearchNoMatches } from "components/search-no-matches";
import { ResponsiveBreakpoint, ResponsiveBreakpointType } from "constants/responsive-breakpoint";
import { Right } from "constants/right";
import { filterAgent, sortAgents } from "helpers/agent";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { canEditAgent } from "helpers/rights";
import { setDocumentTitle } from "helpers/routing";
import { accountIsCanceled, getSubscriptionPhase, isExpired } from "helpers/subscription";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { IInvitationMode } from "hooks/use-organizations";
import { IStoreState } from "interfaces/storeState";
import {
  IAgent,
  selectAgentsAlphabetically,
  selectIsAdmin,
  selectIsOrganizationOwner,
  selectIsViewer,
  selectLoggedInAgent,
} from "store/entities/agents";
import { IBilling, getBilling } from "store/entities/billing";
import { selectHasLiveChatIntegration, selectIsManagedByShopify } from "store/entities/integrations";
import { ISubscription, selectCurrentSubscription } from "store/entities/subscriptions";
import { ITeam, selectTeamsAlphabetically } from "store/entities/teams";
import { hasRight, selectCanInviteAgents, selectCanInviteLiveChatAgents } from "store/features/session";
import {
  AgentsViewActions,
  ILiveChatAgent,
  ISort,
  getAreAgentsSaved,
  getFocusedColumn,
  getListSorting,
  getSelectedAgentId,
  getSortLoggedInAgentOnTop,
  selectLiveChatAgentsToImportAlphabetically,
} from "store/views/agents";
import { selectCanAddTeams } from "store/views/teams";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";
import { legacyTheme } from "themes/legacyTheme";

import { AddOrConfirmModal } from "./AddOrConfirmModal";
import AgentsDetails from "./AgentDetails";
import { AutoAssignmentPromo } from "./AutoAssignmentPromoModal";
import TopWrapper from "./TopWrapper";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import AgentsList from "./agents-list/AgentsList";
import { agentTeams } from "./helpers";
import { EmptyStateContainer } from "./styled";

interface IOwnProps {
  showAddModalOnInit: boolean;
  invitationMode: IInvitationMode;
  isHandledByShopify: boolean;
}

interface IStateProps {
  agents: IAgent[];
  liveChatAgents: ILiveChatAgent[] | null;
  hasLiveChatIntegration: boolean;
  teams: ITeam[];
  subscription: ISubscription;
  billing: IBilling | null;
  selectedAgentId: string;
  currentAgent: IAgent;
  focusedColumn: number;
  areAgentsSaved: boolean;
  canManageAgents: boolean;
  canInviteAgents: boolean;
  canImportLiveChatAgents: boolean;
  canAddTeams: boolean;
  sort: ISort;
  sortLoggedInAgentOnTop: boolean;
  isOrganizationOwner: boolean;
  isOwner: boolean;
  isViewer: boolean;
}

interface IDispatchProps {
  onSort(field: string, desc: boolean): void;
  onSelectAgent(agentId: string): void;
  onSetFocusedColumn(focusedColumn: number): void;
  onBackToList(): void;
  onFetchLiveChatAgents(): void;
}

const EmptyState: React.FC<{ onSelectAddAgent(): void; canAddAgent: boolean }> = ({
  onSelectAddAgent,
  canAddAgent,
}) => {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleInviteAgents = () => {
    trackEngagement("Agents: Invite agents clicked", EventPlace.Agents, { source: "empty-state" });
    onSelectAddAgent();
  };

  return (
    <EmptyStateContainer>
      <Icon source={PersonAdd} size="xxlarge" />
      <Flex column>
        <Heading>Invite more agents</Heading>
        <Text>Add new members to HelpDesk to solve customer cases together.</Text>
      </Flex>
      <Button disabled={!canAddAgent} kind="primary" size="large" onClick={handleInviteAgents}>
        Invite agents
      </Button>
    </EmptyStateContainer>
  );
};

interface IProps extends IOwnProps, IStateProps, IDispatchProps, IWithBreakpointsProps, RouteComponentProps {}

interface IState {
  filterQuery: string;
  selectedAgentsList: SelectedAgentsList;
  addAgentsModalVisible: boolean;
  addAgentsSuccessModal: boolean;
  isAddDropdownVisible: boolean;
}

export enum SelectedAgentsList {
  LiveChat,
  HelpDesk,
}

const StyledContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const StyledDetailsWrapper = styled.div`
  height: 100%;
  padding: 13px 16px 100px;
  overflow: auto;
  box-sizing: border-box;
`;

const AgentsListWrapper = styled.div`
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

function getFilteredAgents(
  agents: (IAgent | ILiveChatAgent)[],
  sort: ISort,
  filterQuery: string,
  agentOnTop?: IAgent
): (IAgent | ILiveChatAgent)[] {
  const { field, desc } = sort;
  let filteredAgents = [...agents];

  if (filterQuery) {
    filteredAgents = filteredAgents.filter(filterAgent(filterQuery));
  }
  if (field) {
    filteredAgents = filteredAgents.slice().sort(sortAgents(field, desc, agentOnTop));
  }
  return filteredAgents;
}

function isCorrectAgentId(agentId: string, agents: IAgent[]): boolean {
  return agents.some(agent => agent.id === agentId);
}

class AgentsComponent extends React.PureComponent<IProps, IState> {
  state = {
    filterQuery: "",
    selectedAgentsList: SelectedAgentsList.LiveChat,
    addAgentsModalVisible: this.props.showAddModalOnInit,
    addAgentsSuccessModal: false,
    isAddDropdownVisible: false,
  };

  componentDidMount(): void {
    trackEngagement("Agents: Page opened", EventPlace.Agents);
    this.updateAgentRoute();
  }

  componentDidUpdate(prevProps: IProps): void {
    const { agents, selectedAgentId, match } = this.props;
    const routeParams: { id?: string } = match.params;

    const agentId = routeParams.id;
    if (agentId !== selectedAgentId || !isCorrectAgentId(selectedAgentId, agents)) {
      this.updateAgentRoute();
      return;
    }

    if (prevProps.areAgentsSaved === false && this.props.areAgentsSaved === true) {
      this.handleToggleSuccessModal();
    }

    setDocumentTitle("Agents");
  }

  handleFilterAgents = (filterQuery: string): void => {
    this.setState({ filterQuery });
  };

  handleSetSelectedAgent = (agentLogin: string): void => {
    this.props.onSelectAgent(agentLogin);
    this.props.onSetFocusedColumn(1);
    this.props.history.push(`/agents/${encodeURIComponent(agentLogin)}`);
  };

  handleDoubleClickAgent = (agentLogin: string): void => {
    const { canManageAgents } = this.props;

    if (canManageAgents) {
      this.props.history.push(`/agents/${encodeURIComponent(agentLogin)}/edit`);
    }
  };

  handleBackToList = (): void => {
    this.props.onBackToList();
  };

  handleSelectedAgentsListChange = (selectedAgentsList: SelectedAgentsList): void => {
    this.setState({ selectedAgentsList });
  };

  forceNavigationToAgent(agentId: string): void {
    this.props.history.replace(`/agents/${encodeURIComponent(agentId)}`);
  }

  updateAgentRoute(): void {
    const { agents, sort, selectedAgentId, location, currentAgent, sortLoggedInAgentOnTop } = this.props;
    const { pathname } = location;
    const { filterQuery } = this.state;
    const openModalOnEntry = pathname.endsWith("invite");

    const routeParams: { id?: string } = this.props.match.params;
    let agentId = routeParams.id || selectedAgentId;
    if (!isCorrectAgentId(agentId, agents)) {
      const filteredAgents = getFilteredAgents(
        agents,
        sort,
        filterQuery,
        sortLoggedInAgentOnTop ? currentAgent : undefined
      );
      agentId = filteredAgents[0].id;
    }

    if (openModalOnEntry && currentAgent.permission === "owner") {
      this.handleSelectAddAgent();
    }
    this.props.onSelectAgent(agentId);
    this.forceNavigationToAgent(agentId);
  }

  handleToggleAddAgentsModal = (): void => {
    this.setState(prevState => ({
      addAgentsModalVisible: !prevState.addAgentsModalVisible,
      addAgentsSuccessModal: false,
    }));
  };

  handleToggleSuccessModal = (): void => {
    this.setState(prevState => ({
      addAgentsModalVisible: false,
      addAgentsSuccessModal: !prevState.addAgentsSuccessModal,
    }));
  };

  handleAddAgentsManually = (eventName: string): void => {
    trackEngagement(eventName, "Agents");
    this.handleToggleAddAgentsModal();
  };

  handleAddDropdownToggle = (): void => {
    this.setState(prevState => ({
      isAddDropdownVisible: !prevState.isAddDropdownVisible,
    }));
  };

  handleSelectAddAgent = (): void => {
    const { currentAgent, subscription, billing } = this.props;
    this.setState(prevState => ({
      addAgentsModalVisible: !prevState.addAgentsModalVisible,
      addAgentsSuccessModal: false,
      isAddDropdownVisible: false,
    }));
    trackEngagement("Agents: Add new agents clicked", EventPlace.Agents, {
      role: currentAgent.permission,
      subscriptionPhase: getSubscriptionPhase(subscription, billing),
    });
  };

  handleAddAgentSelect = (): void => {
    trackEngagement("Agents: Invite agents clicked", EventPlace.Agents, { source: "top-bar" });
    this.handleSelectAddAgent();
  };

  render(): JSX.Element {
    const { filterQuery, selectedAgentsList, addAgentsSuccessModal, addAgentsModalVisible, isAddDropdownVisible } =
      this.state;

    const {
      agents,
      liveChatAgents,
      sort,
      hasLiveChatIntegration,
      subscription,
      billing,
      selectedAgentId,
      currentAgent,
      focusedColumn,
      canManageAgents,
      canInviteAgents,
      canImportLiveChatAgents,
      canAddTeams,
      teams,
      areAgentsSaved,
      isMobile,
      onSort,
      sortLoggedInAgentOnTop,
      onFetchLiveChatAgents,
      isOwner,
      isHandledByShopify,
    } = this.props;

    const filteredHelpDeskAgents = getFilteredAgents(
      agents,
      sort,
      filterQuery,
      sortLoggedInAgentOnTop ? currentAgent : undefined
    ) as IAgent[];
    const filteredLiveChatAgents = liveChatAgents
      ? (getFilteredAgents(liveChatAgents, sort, filterQuery) as ILiveChatAgent[])
      : null;
    const selectedAgent = agents.find(agent => agent.id === selectedAgentId);
    const backButton = <BackButton onClick={this.handleBackToList}>Back</BackButton>;
    const subscriptionExpired = accountIsCanceled(billing) || isExpired(subscription);
    const canEdit = selectedAgentId && canEditAgent(selectedAgentId, currentAgent.id, canManageAgents);
    const canAddAgent = (!subscriptionExpired && canInviteAgents) || isHandledByShopify;

    const editButton = (
      <Block marginRight={withVar(SpacingToken.Spacing2)}>
        <StyledLink
          kind="plain"
          disabled={!canEdit || !selectedAgentId}
          to={`/agents/${encodeURIComponent(selectedAgentId)}/edit`}
          size="compact"
          icon={<Icon source={Edit} />}
        />
      </Block>
    );

    const detailsColumnVisible = selectedAgentsList === SelectedAgentsList.LiveChat;
    const gridWidths = detailsColumnVisible ? [3, 1] : [3];
    return (
      <ThemeProvider theme={legacyTheme}>
        <Grid
          relativeWidths={gridWidths}
          minWidths={[320, 320]}
          focusedColumn={focusedColumn}
          mobileLayoutWidth={ResponsiveBreakpoint[ResponsiveBreakpointType.Tablet]}
        >
          <ViewStack>
            <StyledContainer>
              <TopWrapper
                selectedTab="agents"
                agentsCount={agents.length}
                teamsCount={teams.length}
                filterQuery={filterQuery}
                areAgentsSaved={areAgentsSaved}
                canAddAgent={canAddAgent}
                canAddTeam={canAddTeams}
                isMobile={isMobile}
                onFilterQueryChange={this.handleFilterAgents}
                onDropdownToggle={this.handleAddDropdownToggle}
                onAddAgentSelect={this.handleAddAgentSelect}
                isDropdownVisible={isAddDropdownVisible}
                isOwner={isOwner}
              />
              <AgentsListWrapper>
                <AgentsList
                  sort={sort}
                  hasLiveChatIntegration={hasLiveChatIntegration}
                  helpDeskAgentsCount={agents.length}
                  filteredHelpDeskAgents={filteredHelpDeskAgents}
                  liveChatAgentsCount={liveChatAgents ? liveChatAgents.length : 0}
                  filteredLiveChatAgents={filteredLiveChatAgents}
                  onSelectAgent={this.handleSetSelectedAgent}
                  onDoubleClickAgent={this.handleDoubleClickAgent}
                  onFetchLiveChatAgents={onFetchLiveChatAgents}
                  selectedAgent={selectedAgent}
                  selectedAgentsList={selectedAgentsList}
                  onChangeSelectedAgentsList={this.handleSelectedAgentsListChange}
                  onSort={onSort}
                  canImportLiveChatAgents={canImportLiveChatAgents}
                  onAddAgentClick={this.handleSelectAddAgent}
                />
                {filterQuery && filteredHelpDeskAgents.length === 0 && <SearchNoMatches />}
                {!filterQuery && agents.length === 1 && (
                  <EmptyState canAddAgent={canAddAgent} onSelectAddAgent={this.handleSelectAddAgent} />
                )}
              </AgentsListWrapper>
            </StyledContainer>
          </ViewStack>
          {detailsColumnVisible && (
            <ViewStack>
              <ViewContainer title="Details" backButton={backButton} rightNodes={editButton}>
                {selectedAgent && (
                  <StyledDetailsWrapper>
                    <AgentsDetails
                      agent={selectedAgent}
                      teams={agentTeams(selectedAgent, teams)}
                      canReactivateEmail={false}
                    />
                  </StyledDetailsWrapper>
                )}
                {selectedAgentId && !selectedAgent && <SearchNoMatches title="Details are not available" />}
              </ViewContainer>
            </ViewStack>
          )}
        </Grid>
        <AutoAssignmentPromo />
        {addAgentsModalVisible && (
          <ModalPortal zIndex={StackingContextLevel.Modal}>
            <AddOrConfirmModal onClose={this.handleToggleAddAgentsModal} />
          </ModalPortal>
        )}

        {addAgentsSuccessModal && (
          <ModalPortal zIndex={StackingContextLevel.Modal}>
            <ConfirmActionModal
              icon={Check}
              iconColor={withVar(DesignToken.SurfaceAccentEmphasisHighPositive)}
              onClose={this.handleToggleSuccessModal}
              heading="Your invitations have been sent!"
              secondaryAction={{
                onClick: () => this.handleAddAgentsManually("Agents: Invite more people button clicked"),
                label: "Invite more agents",
              }}
              primaryAction={{
                onClick: this.handleToggleSuccessModal,
                label: "Done",
                actionType: "primary",
                dataTestId: "modal-invite-agents-done-button",
              }}
            >
              Your teammates will receive invitation emails.
            </ConfirmActionModal>
          </ModalPortal>
        )}
      </ThemeProvider>
    );
  }
}

/* eslint-disable react-redux/mapStateToProps-prefer-hoisted */
const mapStateToProps = (state: IStoreState): IStateProps => ({
  agents: selectAgentsAlphabetically(state),
  // @ts-expect-error TS(2322)
  liveChatAgents: selectLiveChatAgentsToImportAlphabetically(state),
  hasLiveChatIntegration: selectHasLiveChatIntegration(state),
  teams: selectTeamsAlphabetically(state),
  subscription: selectCurrentSubscription(state),
  billing: getBilling(state),
  selectedAgentId: getSelectedAgentId(state),
  currentAgent: selectLoggedInAgent(state),
  focusedColumn: getFocusedColumn(state),
  areAgentsSaved: getAreAgentsSaved(state),
  canManageAgents: hasRight(state, Right.ManageAgents),
  canInviteAgents: selectCanInviteAgents(state),
  canImportLiveChatAgents: selectCanInviteLiveChatAgents(state),
  canAddTeams: selectCanAddTeams(state),
  sort: getListSorting(state),
  sortLoggedInAgentOnTop: getSortLoggedInAgentOnTop(state),
  isOrganizationOwner: selectIsOrganizationOwner(state),
  isOwner: selectIsAdmin(state),
  isViewer: selectIsViewer(state),
  isHandledByShopify: selectIsManagedByShopify(state),
});
/* eslint-enable react-redux/mapStateToProps-prefer-hoisted */

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  // @ts-expect-error TS(2345)
  onSort: (field: string, desc: boolean) => dispatch(AgentsViewActions.setListSorting({ field, desc })),
  // @ts-expect-error TS(2345)
  onSelectAgent: (selectedAgentId: string) => dispatch(AgentsViewActions.setSelectedAgent({ selectedAgentId })),
  // @ts-expect-error TS(2345)
  onSetFocusedColumn: (focusedColumn: number) => dispatch(AgentsViewActions.setFocusedColumn(focusedColumn)),
  // @ts-expect-error TS(2345)
  onBackToList: () => dispatch(AgentsViewActions.setFocusedColumn(0)),
  // @ts-expect-error TS(2345)
  onFetchLiveChatAgents: () => dispatch(AgentsViewActions.fetchLiveChatAgents()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withBreakpoints(AgentsComponent));
